import SSOButton from './SSOButton';
import { AuthStepPanel } from './AuthStepPanel';

const S3_UPLOADS_BASE_URL = 'https://bquest-app-uploads.s3.amazonaws.com';

type CompanyAuthMethodsCardProps = {
  animationKey: string;
  companyName?: string | null;
  companyLogoUrl?: string | null;
  email: string;
  emailMode: 'summary' | 'input' | 'hidden';
  showPlaceholder?: boolean;
  onEmailChange?: (value: string) => void;
  onEmailFocus?: () => void;
  onEmailBlur?: () => void;
  onChangeEmail?: () => void;
  showOtpAction: boolean;
  otpButtonLabel?: string;
  onOtpAction?: () => void;
  showSsoAction: boolean;
  companySlug?: string | null;
  connectionId?: string | null;
  orgCode?: string | null;
  isBusy?: boolean;
  busyLabel?: string;
  authError?: string | null;
  onError?: (message: string | null) => void;
};

const resolveCompanyLogoUrl = (logoUrl: string | null | undefined) => {
  if (!logoUrl) return null;
  if (logoUrl.startsWith('http://') || logoUrl.startsWith('https://')) {
    return logoUrl;
  }
  return `${S3_UPLOADS_BASE_URL}/${logoUrl}`;
};

export const CompanyAuthMethodsCard = ({
  animationKey,
  companyName,
  companyLogoUrl,
  email,
  emailMode,
  showPlaceholder = true,
  onEmailChange,
  onEmailFocus,
  onEmailBlur,
  onChangeEmail,
  showOtpAction,
  otpButtonLabel = 'Continue',
  onOtpAction,
  showSsoAction,
  companySlug,
  connectionId,
  orgCode,
  isBusy = false,
  busyLabel = 'Connecting...',
  authError,
  onError,
}: CompanyAuthMethodsCardProps) => {
  const resolvedLogoUrl = resolveCompanyLogoUrl(companyLogoUrl);
  const normalizedEmail = email.trim().toLowerCase();

  return (
    <AuthStepPanel
      title=""
      description=""
      animationKey={animationKey}
    >
      <div className="space-y-4">
        {companyName && (
          <div className="rounded-[1.4rem] border border-white/12 bg-white/[0.04] p-4">
            <div className="flex flex-col items-center gap-4 text-center sm:flex-row sm:text-left">
              {resolvedLogoUrl && (
                <img
                  src={resolvedLogoUrl}
                  alt={`${companyName} logo`}
                  className="h-16 w-16 rounded-[1.15rem] bg-white p-2.5 object-contain"
                />
              )}
              <div>
                <p className="font-termina text-[11px] uppercase tracking-[0.22em] text-white/60">
                  Company
                </p>
                <h4 className="mt-1 font-recoleta text-[1.9rem] leading-tight text-white">
                  {companyName}
                </h4>
              </div>
            </div>
          </div>
        )}

        {emailMode === 'summary' && (
          <div className="flex flex-col gap-3 rounded-[1.3rem] border border-white/10 bg-black/10 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0 flex-1">
              <p className="font-termina text-[11px] uppercase tracking-[0.18em] text-white/55">
                Email
              </p>
              <p className="mt-1 truncate font-termina text-base font-semibold text-white">
                {email}
              </p>
            </div>
            {onChangeEmail && (
              <button
                type="button"
                onClick={onChangeEmail}
                className="w-max shrink-0 rounded-full border border-white/20 px-4 py-2 font-termina text-[11px] font-semibold uppercase tracking-[0.16em] text-white transition-colors hover:bg-white/10"
              >
                Change
              </button>
            )}
          </div>
        )}

        {emailMode === 'input' && (
          <div className="flex flex-col gap-2">
            <label
              htmlFor="company-auth-email"
              className="font-termina text-sm uppercase tracking-[0.12em] text-white"
            >
              Email Address
            </label>
            <input
              id="company-auth-email"
              className="w-full rounded-[1.65rem] px-5 py-3.5 font-termina text-base text-slate-900 md:max-w-[24rem]"
              type="email"
              placeholder={showPlaceholder ? 'Email address' : ''}
              value={email}
              onFocus={onEmailFocus}
              onBlur={onEmailBlur}
              onChange={(event) => onEmailChange?.(event.target.value)}
            />
          </div>
        )}

        <div className="flex flex-col gap-3">
          {showOtpAction && (
            <button
              type="button"
              onClick={onOtpAction}
              disabled={isBusy || !normalizedEmail}
              className="w-full max-w-[24rem] rounded-[1.65rem] bg-[#E2A807] px-6 py-3 font-termina font-black uppercase text-slate-950 transition-all duration-200 hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isBusy ? busyLabel : otpButtonLabel}
            </button>
          )}

          {showSsoAction && companySlug && (
            <SSOButton
              companySlug={companySlug}
              connectionId={connectionId}
              orgCode={orgCode}
              email={normalizedEmail || undefined}
              onError={onError}
              error={authError || null}
              disabled={isBusy}
            />
          )}

          {authError && !showSsoAction && (
            <p className="font-termina text-sm text-red-200" role="alert">
              {authError}
            </p>
          )}
        </div>
      </div>
    </AuthStepPanel>
  );
};
